import { memo, useState, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import { Search, X, ShoppingCart, Menu } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import aquaQuoteLogo from "@/assets/aquaquote-logo.png";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { cn } from "@/lib/utils";
import { getProductUrl } from "@/lib/productUrl";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// Lazy load CartDrawer - only needed when user interacts
const CartDrawer = lazy(() => import("@/components/CartDrawer").then(m => ({ default: m.CartDrawer })));

const navItems = [
  { path: "/", label: "Home" },
  { path: "/pressure-vessels", label: "Pressure Vessels" },
  { path: "/ro-membranes", label: "RO Membranes" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact Us" },
  { path: "/blog", label: "Blog" },
];

// Memoized desktop navigation links
const NavLinks = memo(() => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        {navItems.map((item) => (
          <NavigationMenuItem key={item.path}>
            <NavigationMenuLink 
              asChild 
              className={cn(
                navigationMenuTriggerStyle(), 
                isActive(item.path) && "scale-105 underline underline-offset-4 decoration-2 decoration-primary"
              )}
            >
              <Link to={item.path}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
});

NavLinks.displayName = "NavLinks";

// Mobile navigation component
const MobileNav = memo(() => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10">
          <Menu className="h-7 w-7" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] sm:w-[320px]">
        <SheetHeader>
          <SheetTitle>
            <Link to="/" onClick={() => setOpen(false)}>
              <img 
                src={aquaQuoteLogo} 
                alt="AquaQuote" 
                className="h-10 w-[100px]"
                width="100"
                height="40"
              />
            </Link>
          </SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col gap-2 mt-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={cn(
                "px-4 py-3 rounded-md text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                isActive(item.path) && "bg-accent text-accent-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
});

MobileNav.displayName = "MobileNav";

export const Header = memo(() => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use React Query for better caching
  const { data: products = [] } = useQuery({
    queryKey: ['header-products'],
    queryFn: () => fetchProducts(250),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Memoize filtered products
  const filteredProducts = useMemo(() => {
    if (searchQuery.length < 3) return [];
    
    const searchTerm = searchQuery.toLowerCase().trim();
    return products.filter((product: any) => {
      const title = product.node.title.toLowerCase();
      const vendor = product.node.vendor?.toLowerCase() || '';
      return title.includes(searchTerm) || vendor.includes(searchTerm);
    });
  }, [products, searchQuery]);

  const handleProductSelect = useCallback((product: any) => {
    setShowResults(false);
    setSearchQuery("");
    const url = getProductUrl({
      handle: product.node.handle,
      vendor: product.node.vendor,
      productType: product.node.productType,
      title: product.node.title,
    });
    navigate(url);
  }, [navigate]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.length >= 3);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setShowResults(false);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 sm:h-20 items-center justify-between gap-2 sm:gap-4 px-4">
        {/* Mobile menu */}
        <MobileNav />
        
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img 
            src={aquaQuoteLogo} 
            alt="AquaQuote" 
            className="h-10 w-[100px] sm:h-12 sm:w-[120px]"
            width="120"
            height="48"
          />
        </Link>

        {/* Desktop nav */}
        <NavLinks />

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Search - hidden on mobile, shown on tablet+ */}
          <div className="relative hidden md:block w-48 lg:w-64" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Search by Part Number"
              className="w-full pl-10 pr-10 text-sm"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery.length >= 3 && setShowResults(true)}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            
            {/* Search Results Dropdown */}
            {showResults && (
              <div className="absolute top-full mt-2 w-full bg-popover border rounded-md shadow-lg max-h-96 overflow-auto z-50">
                {searchQuery.length < 3 ? (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    Type at least 3 characters to search
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground text-center">
                    No products found
                  </div>
                ) : (
                  <div className="py-2">
                    {filteredProducts.map((product: any) => (
                      <button
                        key={product.node.id}
                        onClick={() => handleProductSelect(product)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors text-left"
                      >
                        {product.node.images.edges[0] && (
                          <img 
                            src={product.node.images.edges[0].node.url} 
                            alt={product.node.title}
                            className="w-12 h-12 object-cover rounded flex-shrink-0"
                            loading="lazy"
                            width={48}
                            height={48}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{product.node.title}</div>
                          <div className="text-sm text-muted-foreground">
                            ${parseFloat(product.node.priceRange.minVariantPrice.amount).toFixed(2)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Cart */}
          <Suspense fallback={
            <Button variant="ghost" size="icon" className="relative">
              <ShoppingCart className="h-5 w-5" />
            </Button>
          }>
            <CartDrawer />
          </Suspense>
        </div>
      </div>
    </header>
  );
});

Header.displayName = "Header";
